<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="2" />
    <h4>Trámites por Fechas de Atención</h4>
    <hr />

    <!-- Filtros rápidos de fecha -->
    <div class="alert alert-info py-2 px-3 mb-3" role="alert">
      <strong>Instrucciones:</strong>
      Seleccione un rango rápido o ingrese las fechas manualmente y presione
      <strong>Consultar</strong>.
    </div>

    <div class="container mb-4">
      <div class="row g-3">
        <!-- Botones de rango rápido -->
        <div class="col-12 d-flex flex-wrap gap-2">
          <button class="btn btn-outline-primary  btn-sm" @click="setToday">Hoy</button>
          <button class="btn btn-outline-danger   btn-sm" @click="setThisWeek">Semana Actual</button>
          <button class="btn btn-outline-secondary btn-sm" @click="setThisMonth">Mes Actual</button>
          <button class="btn btn-outline-success  btn-sm" @click="setThisQuarter">Trimestre Actual</button>
          <button class="btn btn-outline-info     btn-sm" @click="setThisSemester">Semestre Actual</button>
          <button class="btn btn-outline-warning  btn-sm" @click="setThisYear">Año Actual</button>
        </div>

        <!-- Inputs de fecha -->
        <div class="col-auto">
          <label class="form-label fw-bold">Desde</label>
          <input type="date" class="form-control" v-model="fechaDesde" />
        </div>
        <div class="col-auto">
          <label class="form-label fw-bold">Hasta</label>
          <input type="date" class="form-control" v-model="fechaHasta" />
        </div>

        <!-- Acciones -->
        <div class="col-auto d-flex align-items-end gap-2">
          <button class="btn btn-dark" @click="fetchTramites">
            <i class="material-icons me-1" style="font-size:18px;vertical-align:middle">search</i>
            Consultar
          </button>
          <button
            class="btn btn-outline-success"
            @click="exportToCSV"
            :disabled="tramites.length === 0"
          >
            <i class="material-icons me-1" style="font-size:18px;vertical-align:middle">download</i>
            Exportar CSV
          </button>
        </div>
      </div>
    </div>

    <!-- Selector registros por página -->
    <div class="d-flex justify-content-end align-items-center gap-2 mb-2" v-if="tramites.length > 0">
      <label class="form-label mb-0 small fw-bold">Mostrar</label>
      <select
        v-model.number="elementosPorPagina"
        class="form-select form-select-sm"
        style="width:75px"
        @change="aplicarPaginacion(1)"
      >
        <option :value="10">10</option>
        <option :value="25">25</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
      <span class="small text-muted">registros</span>
    </div>

    <!-- Tabla -->
    <div class="table-responsive" v-if="tramites.length > 0">
      <table class="table table-hover table-bordered table-sm">
        <thead class="table-dark">
          <tr>
            <th class="th-sort" @click="ordenarPor('idTramite')">
              Id Trámite <span>{{ iconOrden('idTramite') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('nombreComercial')">
              Prestador de Salud <span>{{ iconOrden('nombreComercial') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('descripcion')">
              Descripción <span>{{ iconOrden('descripcion') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('estadoTramite')">
              Estado <span>{{ iconOrden('estadoTramite') }}</span>
            </th>
            <th class="th-sort text-end" @click="ordenarPor('numeroPlanillas')">
              N° Planillas <span>{{ iconOrden('numeroPlanillas') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tramite in datosPaginados" :key="tramite.idTramite">
            <td class="text-center">{{ tramite.idTramite }}</td>
            <td>{{ tramite.nombreComercial }}</td>
            <td>{{ tramite.descripcion }}</td>
            <td>
              <span class="badge" :class="badgeEstado(tramite.estadoTramite)">
                {{ tramite.estadoTramite }}
              </span>
            </td>
            <td class="text-end">{{ tramite.numeroPlanillas }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="table-secondary">
            <td colspan="5" class="text-end fw-bold small">
              Mostrando {{ datosPaginados.length }} de {{ tramitesOrdenados.length }} trámites
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Sin resultados -->
    <div v-if="sinResultados" class="alert alert-warning mt-3" role="alert">
      No se recuperaron registros.
    </div>

    <!-- Paginación -->
    <div
      class="d-flex justify-content-between align-items-center mt-3"
      v-if="tramites.length > 0"
    >
      <span class="small text-muted">
        Página {{ paginaActual }} de {{ totalPaginas() }} —
        {{ tramitesOrdenados.length }} registros
      </span>
      <nav aria-label="Paginación">
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: paginaActual === 1 }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(1)">«</a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === 1 }">
            <a class="page-link" href="#" @click.prevent="getPreviousPage">‹</a>
          </li>
          <li
            v-for="pagina in paginasVisibles"
            :key="pagina"
            class="page-item"
            :class="{ active: pagina === paginaActual, disabled: pagina === '...' }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="pagina !== '...' && aplicarPaginacion(pagina)"
            >
              {{ pagina }}
            </a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === totalPaginas() }">
            <a class="page-link" href="#" @click.prevent="getNextPage">›</a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === totalPaginas() }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(totalPaginas())">»</a>
          </li>
        </ul>
      </nav>
    </div>

    <Footer />
  </div>
</template>

<script>
import Layout from "@/components/Layout.vue";
import Footer from "@/components/Footer.vue";
import api from "@/services/api";
import {
  getToday,
  getThisWeek,
  getThisMonth,
  getThisQuarter,
  getThisSemester,
  getThisYear,
} from "@/modules/queryDateUtils.js";

export default {
  components: { Layout, Footer },
  data() {
    return {
      fechaDesde:         "",
      fechaHasta:         "",
      tramites:           [],
      idEmpresa:          0,
      sinResultados:      false,
      // Paginación
      elementosPorPagina: 10,
      datosPaginados:     [],
      paginaActual:       1,
      // Ordenamiento
      campoOrden:         "idTramite",
      ascendente:         false,
    };
  },
  computed: {
    tramitesOrdenados() {
      const campo = this.campoOrden;
      return [...this.tramites].sort((a, b) => {
        let va = a[campo] ?? "";
        let vb = b[campo] ?? "";
        if (campo === "idTramite" || campo === "numeroPlanillas") {
          const na = parseFloat(va);
          const nb = parseFloat(vb);
          if (!isNaN(na) && !isNaN(nb))
            return this.ascendente ? na - nb : nb - na;
        }
        const cmp = String(va).localeCompare(String(vb), "es");
        return this.ascendente ? cmp : -cmp;
      });
    },
    paginasVisibles() {
      const total  = this.totalPaginas();
      const actual = this.paginaActual;
      const delta  = 2;
      const rango  = [];
      for (
        let i = Math.max(2, actual - delta);
        i <= Math.min(total - 1, actual + delta);
        i++
      ) {
        rango.push(i);
      }
      if (rango.length && rango[0] > 2)                        rango.unshift("...");
      if (rango.length && rango[rango.length - 1] < total - 1) rango.push("...");
      const paginas = [1];
      rango.forEach((p) => paginas.push(p));
      if (total > 1) paginas.push(total);
      return paginas;
    },
  },
  mounted() {
    this.idEmpresa = window.sessionStorage.getItem("idEmpresa");
    this.$refs.layoutRef.toggleProcesos();
    this.$refs.layoutRef.toggleReportes();
    this.$refs.layoutRef.toggleParametros();
    this.$refs.layoutRef.toggleAyuda();
  },
  methods: {
    // ── Ordenamiento ───────────────────────────────────────
    ordenarPor(campo) {
      if (this.campoOrden === campo) {
        this.ascendente = !this.ascendente;
      } else {
        this.campoOrden = campo;
        this.ascendente = true;
      }
      this.aplicarPaginacion(1);
    },
    iconOrden(campo) {
      if (this.campoOrden !== campo) return "⇅";
      return this.ascendente ? "↑" : "↓";
    },
    badgeEstado(estado) {
      return {
        "bg-secondary":         estado === "ENVIADO",
        "bg-primary":           estado === "RECIBIDO",
        "bg-warning text-dark": estado === "EN PROCESO",
        "bg-info text-dark":    estado === "OBSERVACIONES",
        "bg-success":           estado === "FINALIZADO",
      };
    },
    // ── Paginación ─────────────────────────────────────────
    totalPaginas() {
      return Math.max(
        1,
        Math.ceil(this.tramitesOrdenados.length / this.elementosPorPagina)
      );
    },
    aplicarPaginacion(noPagina) {
      this.paginaActual   = noPagina;
      const ini           = (noPagina - 1) * this.elementosPorPagina;
      this.datosPaginados = this.tramitesOrdenados.slice(
        ini,
        ini + this.elementosPorPagina
      );
    },
    getPreviousPage() {
      if (this.paginaActual > 1) this.aplicarPaginacion(this.paginaActual - 1);
    },
    getNextPage() {
      if (this.paginaActual < this.totalPaginas())
        this.aplicarPaginacion(this.paginaActual + 1);
    },
    // ── Fetch ──────────────────────────────────────────────
    fetchTramites() {
      if (!this.fechaDesde || !this.fechaHasta) return;
      this.sinResultados = false;
      api
        .get(`/vwtramites/por-fecha`, {
          params: {
            idEmpresa:  this.idEmpresa,
            fechaDesde: this.fechaDesde,
            fechaHasta: this.fechaHasta,
          },
        })
        .then((response) => {
          this.tramites      = Array.isArray(response.data) ? response.data : [];
          this.sinResultados = this.tramites.length === 0;
          this.aplicarPaginacion(1);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          this.sinResultados = true;
        });
    },
    // ── Rangos rápidos ─────────────────────────────────────
    setDateRange(fn) {
      const dates     = fn();
      this.fechaDesde = dates.fechaDesde;
      this.fechaHasta = dates.fechaHasta;
      this.fetchTramites();
    },
    setToday()        { this.setDateRange(getToday);        },
    setThisWeek()     { this.setDateRange(getThisWeek);     },
    setThisMonth()    { this.setDateRange(getThisMonth);    },
    setThisQuarter()  { this.setDateRange(getThisQuarter);  },
    setThisSemester() { this.setDateRange(getThisSemester); },
    setThisYear()     { this.setDateRange(getThisYear);     },
    // ── Exportar CSV (separador ";") ───────────────────────
    exportToCSV() {
      const escape = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val).replace(/"/g, '""');
        return str.includes(";") || str.includes("\n") ? `"${str}"` : str;
      };
      const headers = ["Id Trámite", "Prestador de Salud", "Descripción", "Estado", "N° Planillas"];
      const rows = [
        headers.join(";"),
        ...this.tramites.map((t) =>
          [t.idTramite, t.nombreComercial, t.descripcion, t.estadoTramite, t.numeroPlanillas]
            .map(escape)
            .join(";")
        ),
      ];
      const bom  = "\uFEFF";
      const blob = new Blob([bom + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `tramites_${this.fechaDesde}_${this.fechaHasta}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.th-sort:hover {
  background-color: #3d5166;
}
.table-hover tbody tr {
  cursor: pointer;
}
</style>